// @ts-nocheck
import { useState, useCallback, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Modal,
  Textarea,
  Select,
  Badge,
  ActionIcon,
  SimpleGrid,
  Checkbox,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { DateInput } from '@mantine/dates';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconMapPin,
  IconShield,
  IconUser,
  IconClock,
  IconFilter,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { StatCard } from '../../components/StatCard';
import {
  useGeoFences,
  useGeoFenceViolations,
  useReviewViolation,
  useBulkReviewViolations,
} from '../../hooks/useGeoFencing';
import type { GeoFenceViolation } from '../../types/geofencing';

// ── Constants ───────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PENDING_REVIEW', label: 'Pending Review' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

const VIOLATION_STATUS_COLORS: Record<string, string> = {
  PENDING_REVIEW: 'yellow',
  APPROVED: 'green',
  REJECTED: 'red',
};

// ── Helpers ─────────────────────────────────────────────────────────

function formatDate(d: string | null): string | undefined {
  if (!d) return undefined;
  return d;
}

// ── Component ───────────────────────────────────────────────────────

export default function GeoFenceViolationsPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [geoFenceFilter, setGeoFenceFilter] = useState('');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Review modal state
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState<GeoFenceViolation | null>(null);
  const [reviewAction, setReviewAction] = useState<'APPROVED' | 'REJECTED'>('APPROVED');
  const [reviewNote, setReviewNote] = useState('');

  // Bulk review modal state
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<'APPROVED' | 'REJECTED'>('APPROVED');
  const [bulkNote, setBulkNote] = useState('');

  const { data: fences } = useGeoFences({ perPage: 200 });
  const { data: rawViolations, isLoading } = useGeoFenceViolations({
    geoFenceId: geoFenceFilter || undefined,
    status: statusFilter || undefined,
    startDate: formatDate(startDate),
    endDate: formatDate(endDate),
    page,
    perPage: 25,
  });
  const violations = rawViolations as any;
  const reviewMutation = useReviewViolation();
  const bulkReviewMutation = useBulkReviewViolations();

  const geoFenceOptions = useMemo(() => {
    const opts = [{ value: '', label: 'All GeoFences' }];
    if (fences) {
      for (const f of fences) {
        opts.push({ value: f.id, label: f.name });
      }
    }
    return opts;
  }, [fences]);

  const pendingViolations = useMemo(
    () =>
      violations?.geoFenceViolations?.data.filter((v: GeoFenceViolation) => v.status === 'PENDING_REVIEW') ?? [],
    [violations],
  );

  const allPendingSelected = useMemo(
    () =>
      pendingViolations.length > 0 &&
      pendingViolations.every((v: GeoFenceViolation) => selectedIds.has(v.id)),
    [pendingViolations, selectedIds],
  );

  const toggleSelectAll = useCallback(() => {
    if (allPendingSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingViolations.map((v: GeoFenceViolation) => v.id)));
    }
  }, [allPendingSelected, pendingViolations]);

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Single review handlers
  const handleOpenReview = useCallback(
    (violation: GeoFenceViolation, action: 'APPROVED' | 'REJECTED') => {
      setReviewTarget(violation);
      setReviewAction(action);
      setReviewNote('');
      setReviewModalOpen(true);
    },
    [],
  );

  const handleConfirmReview = useCallback(() => {
    if (!reviewTarget) return;
    reviewMutation.mutate(
      {
        id: reviewTarget.id,
        status: reviewAction,
        reviewNote: reviewNote.trim() || undefined,
      },
      {
        onSuccess: () => {
          notifications.show({
            title: reviewAction === 'APPROVED' ? 'Violation Approved' : 'Violation Rejected',
            message: `Violation for ${reviewTarget.employeeName} has been ${reviewAction.toLowerCase()}.`,
            color: reviewAction === 'APPROVED' ? 'green' : 'red',
          });
          setReviewModalOpen(false);
          setReviewTarget(null);
          setSelectedIds((prev) => {
            const next = new Set(prev);
            next.delete(reviewTarget.id);
            return next;
          });
        },
        onError: (error: any) => {
          notifications.show({
            title: 'Review Failed',
            message: error instanceof Error ? error.message : 'An error occurred.',
            color: 'red',
          });
        },
      },
    );
  }, [reviewTarget, reviewAction, reviewNote, reviewMutation]);

  // Bulk review handlers
  const handleOpenBulk = useCallback((action: 'APPROVED' | 'REJECTED') => {
    setBulkAction(action);
    setBulkNote('');
    setBulkModalOpen(true);
  }, []);

  const handleConfirmBulk = useCallback(() => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    bulkReviewMutation.mutate(
      {
        ids,
        status: bulkAction,
        reviewNote: bulkNote.trim() || undefined,
      },
      {
        onSuccess: (result: any) => {
          const data = result.bulkReviewViolations;
          const successCount = bulkAction === 'APPROVED' ? data.approved : data.rejected;
          notifications.show({
            title: 'Bulk Review Complete',
            message: `${successCount} violation(s) ${bulkAction.toLowerCase()}. ${
              data.errors.length > 0 ? `${data.errors.length} error(s) occurred.` : ''
            }`,
            color: data.errors.length > 0 ? 'orange' : 'green',
          });
          setBulkModalOpen(false);
          setSelectedIds(new Set());
        },
        onError: (error: any) => {
          notifications.show({
            title: 'Bulk Review Failed',
            message: error instanceof Error ? error.message : 'An error occurred.',
            color: 'red',
          });
        },
      },
    );
  }, [selectedIds, bulkAction, bulkNote, bulkReviewMutation]);

  const columns: Column<GeoFenceViolation>[] = [
    {
      key: 'select',
      label: '',
      width: 40,
      render: (row) =>
        row.status === 'PENDING_REVIEW' ? (
          <Checkbox
            checked={selectedIds.has(row.id)}
            onChange={() => toggleSelect(row.id)}
            onClick={(e) => e.stopPropagation()}
          />
        ) : null,
    },
    {
      key: 'employeeName',
      label: 'Employee',
      sortable: true,
      render: (row) => (
        <Group gap="xs">
          <IconUser size={14} />
          <Text size="sm">{row.employeeName}</Text>
        </Group>
      ),
    },
    {
      key: 'geoFenceName',
      label: 'GeoFence',
      render: (row) => (
        <Group gap="xs">
          <IconMapPin size={14} />
          <Text size="sm">{row.geoFenceName}</Text>
        </Group>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      width: 120,
      render: (row) => (
        <Badge variant="light" color="blue" size="sm">
          {row.action.replace(/_/g, ' ')}
        </Badge>
      ),
    },
    {
      key: 'distanceFromFence',
      label: 'Distance',
      width: 110,
      sortable: true,
      render: (row) => (
        <Text size="sm" fw={500} c="red">
          {row.distanceFromFence >= 1000
            ? `${(row.distanceFromFence / 1000).toFixed(1)} km`
            : `${Math.round(row.distanceFromFence)} m`}
        </Text>
      ),
    },
    {
      key: 'timestamp',
      label: 'Timestamp',
      width: 170,
      sortable: true,
      render: (row) => (
        <Group gap={4}>
          <IconClock size={12} />
          <Text size="sm">{new Date(row.timestamp).toLocaleString()}</Text>
        </Group>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 140,
      render: (row) => (
        <StatusBadge status={row.status} colorMap={VIOLATION_STATUS_COLORS} />
      ),
    },
  ];

  const summary = violations?.geoFenceViolations?.summary;

  return (
    <Stack gap="md">
      <PageHeader
        title="GeoFence Violations"
        subtitle={
          summary
            ? `${summary.totalViolations} total violation${summary.totalViolations !== 1 ? 's' : ''}`
            : undefined
        }
        breadcrumbs={[
          { label: 'Settings', to: '/admin/workforce/settings' },
          { label: 'GEO Fencing', to: '/admin/workforce/geo-fences' },
          { label: 'Violations' },
        ]}
      />

      {/* Summary stats */}
      {summary && (
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <StatCard
            title="Total Violations"
            value={summary.totalViolations}
            icon={<IconAlertTriangle size={24} />}
            color="blue"
          />
          <StatCard
            title="Pending Review"
            value={summary.pendingCount}
            icon={<IconClock size={24} />}
            color="yellow"
          />
          <StatCard
            title="Approved"
            value={summary.approvedCount}
            icon={<IconCheck size={24} />}
            color="green"
          />
          <StatCard
            title="Rejected"
            value={summary.rejectedCount}
            icon={<IconX size={24} />}
            color="red"
          />
        </SimpleGrid>
      )}

      {/* Filters */}
      <Paper withBorder p="md" radius="md">
        <Group gap="md" ta="flex-end">
          <IconFilter size={18} color="var(--mantine-color-dimmed)" />
          <Select
            label="GeoFence"
            data={geoFenceOptions}
            value={geoFenceFilter}
            onChange={(v) => {
              setGeoFenceFilter(v ?? '');
              setPage(1);
            }}
            w={200}
            clearable
          />
          <Select
            label="Status"
            data={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v ?? '');
              setPage(1);
            }}
            w={180}
          />
          <DateInput
            label="Start Date"
            placeholder="From"
            value={startDate}
            onChange={(v) => {
              setStartDate(v);
              setPage(1);
            }}
            clearable
            w={160}
          />
          <DateInput
            label="End Date"
            placeholder="To"
            value={endDate}
            onChange={(v) => {
              setEndDate(v);
              setPage(1);
            }}
            clearable
            w={160}
          />
          <Button
            variant="subtle"
            size="sm"
            onClick={() => {
              setGeoFenceFilter('');
              setStatusFilter('');
              setStartDate(null);
              setEndDate(null);
              setPage(1);
            }}
          >
            Clear Filters
          </Button>
        </Group>
      </Paper>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <Paper withBorder p="sm" radius="md" bg="blue.0">
          <Group justify="space-between">
            <Group gap="sm">
              <Checkbox
                checked={allPendingSelected}
                indeterminate={selectedIds.size > 0 && !allPendingSelected}
                onChange={toggleSelectAll}
                label={`${selectedIds.size} violation${selectedIds.size !== 1 ? 's' : ''} selected`}
              />
            </Group>
            <Group gap="sm">
              <Button
                size="sm"
                color="green"
                variant="light"
                icon={<IconCheck size={16} />}
                onClick={() => handleOpenBulk('APPROVED')}
              >
                Approve Selected
              </Button>
              <Button
                size="sm"
                color="red"
                variant="light"
                icon={<IconX size={16} />}
                onClick={() => handleOpenBulk('REJECTED')}
              >
                Reject Selected
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      <DataTable<GeoFenceViolation>
        columns={columns}
        data={(violations?.geoFenceViolations?.data as GeoFenceViolation[]) ?? []}
        loading={isLoading}
        pagination={
          violations
            ? {
                page,
                pageSize: 25,
                total: violations.geoFenceViolations.total,
                onPageChange: setPage,
              }
            : undefined
        }
        getRowKey={(row) => row.id}
        emptyMessage="No violations found matching the current filters."
        actions={(row) =>
          row.status === 'PENDING_REVIEW' ? (
            <Group gap="xs" wrap="nowrap">
              <ActionIcon
                variant="subtle"
                color="green"
                onClick={() => handleOpenReview(row, 'APPROVED')}
                title="Approve"
              >
                <IconCheck size={16} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => handleOpenReview(row, 'REJECTED')}
                title="Reject"
              >
                <IconX size={16} />
              </ActionIcon>
            </Group>
          ) : (
            <Text size="xs" c="dimmed">
              {row.reviewedBy ? `By ${row.reviewedBy}` : '--'}
            </Text>
          )
        }
      />

      {/* Single Review Modal */}
      <Modal
        opened={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        title={
          reviewAction === 'APPROVED'
            ? 'Approve Violation'
            : 'Reject Violation'
        }
        centered
        size="sm"
      >
        <Stack gap="md">
          {reviewTarget && (
            <Paper withBorder p="sm" radius="md" bg="gray.0">
              <Stack gap={4}>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    Employee
                  </Text>
                  <Text size="sm">{reviewTarget.employeeName}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    GeoFence
                  </Text>
                  <Text size="sm">{reviewTarget.geoFenceName}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    Action
                  </Text>
                  <Text size="sm">{reviewTarget.action.replace(/_/g, ' ')}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    Distance
                  </Text>
                  <Text size="sm" c="red" fw={500}>
                    {reviewTarget.distanceFromFence >= 1000
                      ? `${(reviewTarget.distanceFromFence / 1000).toFixed(1)} km`
                      : `${Math.round(reviewTarget.distanceFromFence)} m`}{' '}
                    away
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    Time
                  </Text>
                  <Text size="sm">
                    {new Date(reviewTarget.timestamp).toLocaleString()}
                  </Text>
                </Group>
              </Stack>
            </Paper>
          )}

          <Textarea
            label="Review Note (optional)"
            placeholder="Add context for this decision..."
            minRows={3}
            value={reviewNote}
            onChange={(e) => setReviewNote(e.currentTarget.value)}
          />

          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setReviewModalOpen(false)}
              disabled={reviewMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color={reviewAction === 'APPROVED' ? 'green' : 'red'}
              onClick={handleConfirmReview}
              loading={reviewMutation.isPending}
              icon={
                reviewAction === 'APPROVED' ? (
                  <IconCheck size={16} />
                ) : (
                  <IconX size={16} />
                )
              }
            >
              {reviewAction === 'APPROVED' ? 'Approve' : 'Reject'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Bulk Review Modal */}
      <Modal
        opened={bulkModalOpen}
        onClose={() => setBulkModalOpen(false)}
        title={`Bulk ${bulkAction === 'APPROVED' ? 'Approve' : 'Reject'} Violations`}
        centered
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            You are about to {bulkAction.toLowerCase()}{' '}
            <Text span fw={700}>
              {selectedIds.size}
            </Text>{' '}
            violation{selectedIds.size !== 1 ? 's' : ''}. This action will be
            recorded in the audit log.
          </Text>

          <Textarea
            label="Review Note (optional)"
            placeholder="Add context for this bulk decision..."
            minRows={3}
            value={bulkNote}
            onChange={(e) => setBulkNote(e.currentTarget.value)}
          />

          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setBulkModalOpen(false)}
              disabled={bulkReviewMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color={bulkAction === 'APPROVED' ? 'green' : 'red'}
              onClick={handleConfirmBulk}
              loading={bulkReviewMutation.isPending}
              icon={
                bulkAction === 'APPROVED' ? (
                  <IconCheck size={16} />
                ) : (
                  <IconX size={16} />
                )
              }
            >
              {bulkAction === 'APPROVED' ? 'Approve' : 'Reject'} {selectedIds.size}{' '}
              Violation{selectedIds.size !== 1 ? 's' : ''}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
